import React, { useState, useEffect } from "react";
import { useTranslation, withTranslation } from "react-i18next";

import InputMask from "react-input-mask";

import moment from "moment";

/* utils */
import { checkIsMobile } from "../../utils/checkmobile";

import { CONSTANTS } from "../../constants";
import { Tooltip, Button, Divider, Grid, IconButton, Typography } from "@mzc-pdc/ui";
import theme from "@styles/theme";
import { IconCancel, IconInfoCircle, IconInfoLinear } from "@mzc-cloudplex/icons";
import { useDispatch, useSelector } from "react-redux";
import compareVersions, { VERSION_CM_4923_FILE_META_SEARCH } from "@cores/version";
import { getKeys } from "@modules/fileMetaAttributions";
import { RangePicker } from "@components/datepicker";
import WithSelect from "@components/select";

const MAXIMUM_ATTRIBUTIONS = 10;
const isMobile = checkIsMobile();

const FileMetaAttributions = ({ attributions, onFileMetaUpdated }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const stage = useSelector(({ stage }) => stage);
    const fileMetaAttributions = useSelector(({ fileMetaAttributions }) => fileMetaAttributions);

    const [fileMetaKeyOptions, setFileMetaKeyOptions] = useState([]);

    const onClickAddAttribution = () => {
        if (attributions.length === MAXIMUM_ATTRIBUTIONS) return;

        attributions.push({
            key: fileMetaKeyOptions && fileMetaKeyOptions.length > 0 ? fileMetaKeyOptions[0].value : "",
            operator: "CONTAINS",
            values: [{ value: "" }, { value: "" }],
            isEmpty: true,
        });
        onConditionsUpdated();
    };
    const onClickRemoveAttribution = (index) => {
        attributions.splice(index, 1);
        onConditionsUpdated();
    };
    const onKeyChanged = (index, option) => {
        const attribution = attributions[index];
        attribution.key = option.value;
        attribution.sampleValue = option.sampleValue;
        onConditionsUpdated();
    };
    const onOperatorChanged = (index, value) => {
        const attribution = attributions[index];
        attribution.operator = value;
        if (value === "RANGE_DATE") {
            attribution.values = [{ value: null }, { value: null }];
        } else {
            attribution.values = [{ value: "" }, { value: "" }];
        }
        onConditionsUpdated();
    };

    const onValueChanged = (index, valueIndex, value) => {
        const attribution = attributions[index];
        attribution.values[valueIndex].value = value;
        attribution.isEmpty = !value || value.length === 0;
        onConditionsUpdated();
    };

    const renderValueComponent = (attribution, index) => {
        const operator = attribution.operator;
        switch (operator) {
            case "CONTAINS":
                return (
                    <div className={`form-input ${attribution.isEmpty === true ? "form-warning" : ""}`}>
                        <input
                            type="text"
                            value={attribution.values[0].value}
                            onChange={(e) => {
                                onValueChanged(index, 0, e.target.value);
                            }}
                        />
                    </div>
                );
            case "GREATER_THAN":
            case "LESS_THAN":
                return (
                    <div className={`form-input ${attribution.isEmpty === true ? "form-warning" : ""}`}>
                        <input
                            type="number"
                            value={attribution.values[0].value}
                            onChange={(e) => {
                                onValueChanged(index, 0, e.target.value);
                            }}
                        />
                    </div>
                );
            case "RANGE":
                return (
                    <div className={`form-input ${attribution.isEmpty === true ? "form-warning" : ""}`}>
                        <div className="d-flex align-items-center">
                            <input
                                type="text"
                                value={attribution.values[0].value}
                                onChange={(e) => {
                                    onValueChanged(index, 0, e.target.value);
                                }}
                            />
                            <span className="separate"> ~ </span>
                            <input
                                type="text"
                                value={attribution.values[1].value}
                                onChange={(e) => {
                                    onValueChanged(index, 1, e.target.value);
                                }}
                            />
                        </div>
                    </div>
                );
            case "RANGE_DURATION":
                return (
                    <div className={`form-input ${attribution.isEmpty === true ? "form-warning" : ""}`}>
                        <div className="form-group">
                            <InputMask
                                className={"input-group-time"}
                                mask={"99:99:99"}
                                placeholder={"hh:mm:ss"}
                                value={attribution.values[0].value}
                                onChange={(e) => {
                                    onValueChanged(index, 0, e.target.value);
                                }}
                            />
                            <span> ~ </span>
                            <InputMask
                                className={"input-group-time"}
                                mask={"99:99:99"}
                                placeholder={"hh:mm:ss"}
                                value={attribution.values[1].value}
                                onChange={(e) => {
                                    onValueChanged(index, 1, e.target.value);
                                }}
                            />
                        </div>
                    </div>
                );
            case "RANGE_DATE":
                return (
                    <div className={`form-input ${attribution.isEmpty === true ? "form-warning" : ""}`}>
                        <RangePicker
                            startDate={attribution.values[0].value}
                            endDate={attribution.values[1].value}
                            isOutsideRange={() => false}
                            onDatesChange={(date) => {
                                if (date.startDate) {
                                    onValueChanged(index, 0, date.startDate);
                                }
                                if (date.endDate) {
                                    onValueChanged(index, 1, date.endDate);
                                }
                            }}
                        />
                    </div>
                );
        }
    };
    const parseOperator = (operator) => {
        switch (operator) {
            case "CONTAINS":
            case "GREATER_THAN":
            case "LESS_THAN":
                return operator;
            case "RANGE":
            case "RANGE_DURATION":
            case "RANGE_DATE":
                return "BETWEEN";
        }
    };
    const parseValue = (operator, values) => {
        try {
            switch (operator) {
                case "CONTAINS":
                    return `${values[0].value}`;
                case "GREATER_THAN":
                case "LESS_THAN":
                    return Number(values[0].value);
                case "RANGE":
                case "RANGE_DURATION":
                    return `${values[0].value}~${values[1].value}`;
                case "RANGE_DATE": {
                    const leftValue = values[0].value;
                    const rightValue = values[1].value;
                    return `${leftValue ? moment(leftValue).format("YYYY-MM-DDT00:00:00") : ""}~${
                        rightValue ? moment(rightValue).format("YYYY-MM-DDT23:59:59") : ""
                    }`;
                }
            }
        } catch (e) {
            return null;
        }
    };
    const onConditionsUpdated = () => {
        if (!onFileMetaUpdated) return;

        const organizedAttributions = [];
        const length = attributions.length;
        for (let i = 0; i < length; i++) {
            const attribution = attributions[i];
            if (
                attribution.key &&
                attribution.key.length > 0 &&
                attribution.operator &&
                attribution.operator.length > 0
            ) {
                const value = parseValue(attribution.operator, attribution.values);
                if (value)
                    organizedAttributions.push({
                        key: attribution.key,
                        operator: parseOperator(attribution.operator),
                        value,
                    });
            }
        }
        onFileMetaUpdated({
            tempItems: attributions,
            organizedItems: organizedAttributions,
        });
    };

    useEffect(() => {
        if (compareVersions(stage.version, VERSION_CM_4923_FILE_META_SEARCH) >= 0) {
            dispatch(getKeys());
        }
    }, []);

    useEffect(() => {
        const parsedOptions = [];
        if (fileMetaAttributions && fileMetaAttributions.keys && fileMetaAttributions.keys.items) {
            const items = fileMetaAttributions.keys.items;
            const length = items.length;
            for (let i = 0; i < length; i++) {
                const item = items[i];
                const key = item.key;
                parsedOptions.push({
                    label: key,
                    value: key,
                    sampleValue: item.sampleValue,
                });
            }
        }
        setFileMetaKeyOptions(parsedOptions);
    }, [fileMetaAttributions]);

    return (
        <div className="">
            {attributions && attributions.length !== 0 && (
                <div>
                    {attributions && attributions.length !== 0 && !isMobile && (
                        <Grid container gap={1} mb={1}>
                            <Grid item xs={3}>
                                <Typography variant={"body4Bold"} color={theme.palette.text.secondary}>
                                    {t(`common::label::Key`)}
                                </Typography>
                            </Grid>
                            <Grid item xs={3}>
                                <Typography variant={"body4Bold"} color={theme.palette.text.secondary}>
                                    {t(`common::label::Operator`)}
                                </Typography>
                            </Grid>
                            <Grid item xs>
                                <Typography variant={"body4Bold"} color={theme.palette.text.secondary}>
                                    {t(`common::label::Value`)}
                                </Typography>
                            </Grid>
                        </Grid>
                    )}
                    {attributions.map((a, i) => (
                        <React.Fragment key={i}>
                            <Grid
                                container
                                gap={1}
                                flexWrap={"nowrap"}
                                sx={{ mb: 1, ".select2-container": { minWidth: `0` } }}
                            >
                                <Grid item xs={3}>
                                    <WithSelect
                                        placeholder={t(`common::label::Select Key`, "Select Key")}
                                        defaultValue={a.key}
                                        isMulti={false}
                                        options={fileMetaKeyOptions}
                                        hideSelectedOptions={false}
                                        onChange={(option) => {
                                            onKeyChanged(i, option);
                                        }}
                                        menuPortalTarget={document.body}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <WithSelect
                                        defaultValue={a.operator}
                                        isMulti={false}
                                        options={CONSTANTS("VALUE_OPERATOR")}
                                        hideSelectedOptions={false}
                                        onChange={(option) => {
                                            onOperatorChanged(i, option.value);
                                        }}
                                        menuPortalTarget={document.body}
                                    />
                                </Grid>
                                <Grid item xs>
                                    <Grid container flexWrap={"nowrap"} alignItems={"center"} gap={0.5}>
                                        {renderValueComponent(a, i)}
                                        <Tooltip title={t(`common::label::Remove`, `Remove`)}>
                                            <IconButton
                                                circled
                                                size={"medium"}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    onClickRemoveAttribution(i);
                                                }}
                                            >
                                                <IconCancel size={12} htmlColor={theme.palette.secondary[500]} />
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                </Grid>
                            </Grid>

                            {a.sampleValue && (
                                <Grid container alignItems={"center"} gap={1} mt={1}>
                                    <IconInfoLinear size={16} htmlColor={theme.palette.secondary[500]} />
                                    <Typography variant={"body3Regular"} color={theme.palette.text.disabled}>
                                        {a.sampleValue}
                                    </Typography>
                                </Grid>
                            )}
                        </React.Fragment>
                    ))}
                    <Divider sx={{ mt: 1, mb: 1 }} />
                </div>
            )}

            <Button
                variant={"outlined"}
                color={"greySecondary"}
                size={"medium"}
                disabled={attributions.length === 10}
                onClick={(e) => {
                    e.preventDefault();
                    onClickAddAttribution();
                }}
            >
                {t(`common::label::Add`)}
            </Button>
        </div>
    );
};
export default FileMetaAttributions;
